import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InventoryAsset } from '../../../types';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import { DashboardWidgets } from '../../Compounds/DashboardWidgets/DashboardWidgets';
import { DetailsDrawer } from '../../Compounds/DetailsDrawer';
import { getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { getInventoryLoadingSubject, getInventorySubject, useInventory } from '../../../store/Inventory';
import {
  GridPreferences,
  PrefKey,
  UserPreferencesContent,
  getCustomViewSubject,
  getEffectiveAccountId,
  getSelectedAccountsSubject,
  getUser,
  getUserPreferencesSubject,
  isCapabilityDenied,
  isInventoryLocationsViewEnabled,
  isInternalUser,
  shouldSeeSupplierContacts
} from '../../../store/User';
import { CustomFieldCapability } from '../../../utils/customFieldCapabilities';
import { CustomizeDashboardDrawer } from '../../Compounds/CustomizeDashboardDrawer';
import {
  contentWithProductFilters,
  getColumnsByProduct,
  getTabData
} from '../../Compounds/UpstackDataGrid/inventoryUtils';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns } from '../../Compounds/UpstackDataGrid/helpers';
import { AddInventoryLink } from '../../Compounds/AddInventoryLink/AddInventoryLink';
import { LabelledToggle } from '../../Compounds/LabelledToggle';
import { FeatureFlags, getFeatureFlagSubject } from '../../../Api/useFeatureFlags';
import { useProductsStore } from '../../../store/Product';
import { useOutsideInventorySchemasStore } from '../../../store/OutsideInventorySchema';
import { combineLatest } from 'rxjs';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { TabPanel } from '../../Atoms/TabPanel';
import { ColumnGroupSortOrder, SupportNumberDefaults, inventoryRowAccountId } from './inventoryGridColumnDefs';
import { FAILED_SAVE, makeCustomFieldRowUpdate } from '../../Compounds/UpstackDataGrid/customFieldColumns';
import {
  fetchInventoryCustomFieldDefs,
  inventoryCustomFieldColumns,
  useInventoryCustomFieldCellEditable
} from './inventoryCustomFields';
import { GroupedGridColDef } from '../../Compounds/UpstackDataGrid/gridColumnTypes';
import { TAGS_FIELD } from '../../Compounds/UpstackDataGrid/tagColumns';
import { CustomFieldDefinition } from '../../../types';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { GridTabsHeader } from '../../Compounds/GridTabsHeader';
import { SavedViewControls } from '../../Compounds/CustomTableViews/SavedViewControls';
import { LocationsTable } from './LocationsTable';
import { getStoredFlag, storeFlag } from '../../../utils/browserStoredFlag';

const DISPLAY_AS_LOCATIONS_KEY = 'inventoryDisplayAsLocations';

export interface TabProps {
  name: string;
  count: number;
  customView?: boolean;
}
export interface CategorizedData {
  [key: string]: InventoryAsset[];
}

// Ahead of tags, so the tag column still closes the standard set on this tab.
// Generic only so tests can pass bare {field} stubs; that is also what the cast below buys, and its cost
// is that a real shape mismatch between the constant and a caller would not be caught here.
export const withSupplierContactColumns = <T extends { field: string }>(columns: T[], tabName: string): T[] => {
  if (!shouldSeeSupplierContacts() || tabName !== 'All') return columns;
  const tagsIndex = columns.findIndex((col) => col.field === TAGS_FIELD);
  columns.splice(tagsIndex === -1 ? columns.length : tagsIndex, 0, ...(SupportNumberDefaults as unknown as T[]));
  return columns;
};

export const InventoryPage = () => {
  const page = 'Inventory';
  const { setSnack } = useSnackbar();
  const [inventoryAssets, setInventoryAssets] = useState<InventoryAsset[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const selectedTabRef = useRef(0);
  const [tabs, setTabs] = useState<TabProps[]>([]);
  const [allTabs, setAllTabs] = useState<TabProps[]>([]);
  const [customTabNames, setCustomTabNames] = useState<string[]>([]);
  const [categorizedData, setCategorizedData] = useState<CategorizedData>({});
  const [inventoryLoading, setInventoryLoading] = useState<boolean>(false);
  const [columns, setColumns] = useState<GroupedGridColDef[]>();
  const [selectedCustomView, setSelectedCustomView] = useState<string>('');
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const customFieldDefsRef = useRef<CustomFieldDefinition[]>([]);
  const [search] = useSearchParams();
  const detailId = search.get('detailId');
  const { fetchInventory } = useInventory();
  const { fetchProducts } = useProductsStore();
  const { fetchOutsideInventorySchemas } = useOutsideInventorySchemasStore();
  const [userPreferences, setUserPreferences] = useState<UserPreferencesContent>();
  const [open, setOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(getFeatureFlagSubject().getValue());
  const locationsViewEnabled = isInventoryLocationsViewEnabled(featureFlags);
  const [displayAsLocationsChoice, setDisplayAsLocationsChoice] = useState<boolean>(() =>
    getStoredFlag(DISPLAY_AS_LOCATIONS_KEY)
  );
  const displayAsLocations = locationsViewEnabled && displayAsLocationsChoice;

  const handleDisplayAsLocationsChange = (enabled: boolean) => {
    setDisplayAsLocationsChoice(enabled);
    storeFlag(DISPLAY_AS_LOCATIONS_KEY, enabled);
  };

  useEffect(() => {
    const flagsSub = getFeatureFlagSubject().subscribe(setFeatureFlags);
    return () => flagsSub.unsubscribe();
  }, []);

  useEffect(() => {
    selectedTabRef.current = selectedTab;
  }, [selectedTab]);

  useEffect(() => {
    let lastInventoryRef: InventoryAsset[] | null = null;
    let lastTabs: TabProps[] = [];
    let lastCategorizedData: CategorizedData = {};

    const dataSubscription = combineLatest({
      inventory: getInventorySubject(),
      settings: getUserPreferencesSubject(),
      inventoryLoading: getInventoryLoadingSubject(),
      customTabNames: getCustomViewSubject()
    }).subscribe(({ inventory, settings, inventoryLoading, customTabNames }) => {
      // Inventory Assets — only rebuild tabs/categorizedData when the inventory
      // reference actually changes. combineLatest emits on every subject (e.g.
      // inventoryLoading toggling, prefs save), and producing fresh
      // tabs/categorizedData objects on every emission gives DataGrid Pro a new
      // `rows` reference mid-scroll, which corrupts its virtualization state and
      // causes rows to render at duplicate positions (UPDEV-6521).
      let tabs = lastTabs;
      let categorizedData = lastCategorizedData;
      if (inventory !== lastInventoryRef) {
        lastInventoryRef = inventory;
        const result = getTabData(inventory);
        tabs = result.tabs;
        categorizedData = result.categorizedData;
        lastTabs = tabs;
        lastCategorizedData = categorizedData;
        setInventoryAssets(inventory);
        setTabs(tabs);
        setCategorizedData(categorizedData);
      }
      const selectedTabName = tabs[selectedTabRef.current]?.name || 'All';
      const columns = withSupplierContactColumns(getColumnsByProduct(selectedTabName), selectedTabName);
      const newColumns = [
        ...(getGridColumns(
          (categorizedData as CategorizedData)[selectedTabName],
          columns,
          `inventory_grid_${selectedTabName}`
        ) as GroupedGridColDef[]),
        ...inventoryCustomFieldColumns(customFieldDefsRef.current)
      ];
      // Only replace the columns reference when the column structure (fields) has changed.
      // Returning the same reference for data-only updates prevents UpstackDataGrid's
      // columns useEffect from re-sorting columns by saved orderedFields, which would
      // reset any unsaved manual column reordering the user has done in the current session.
      setColumns((prev) => {
        if (prev && prev.length === newColumns.length && prev.every((c, i) => c.field === newColumns[i]?.field)) {
          return prev;
        }
        return newColumns;
      });

      // Inventory Loading
      setInventoryLoading(inventoryLoading);

      // User Settings
      setUserPreferences(
        contentWithProductFilters(
          settings.content,
          tabs.map((t) => t.name),
          [...new Set(inventory.map((i) => i.productDisplay || ''))]
        )
      );
      setCustomTabNames(customTabNames.filter((view) => view.page === 'inventory_grid_').map((v) => v.name));
    });

    fetchInventory();
    fetchProducts();
    fetchOutsideInventorySchemas();

    const loadCustomFieldDefs = (accountId: string) => {
      fetchInventoryCustomFieldDefs(accountId).then((loaded) => {
        // A slower answer for an account switched away from must not replace the current account's set.
        if (!loaded || getEffectiveAccountId() !== accountId) return;
        customFieldDefsRef.current = loaded.definitions;
        setCustomFieldDefs(loaded.definitions);
        if (loaded.relatedInventoryFailed) {
          setSnack({ message: 'Could not load the Related Inventory columns.', type: 'error', open: true });
        }
      });
    };

    const initialAccountId = getEffectiveAccountId();
    if (initialAccountId) loadCustomFieldDefs(initialAccountId);

    const accountSubscription = getSelectedAccountsSubject().subscribe(() => {
      const accountId = getEffectiveAccountId();
      if (accountId) {
        loadCustomFieldDefs(accountId);
      } else {
        customFieldDefsRef.current = [];
        setCustomFieldDefs([]);
      }
    });

    return () => {
      if (dataSubscription) dataSubscription.unsubscribe();
      accountSubscription.unsubscribe();
    };
  }, []);

  // Re-assemble columns when custom field definitions load (they may arrive after inventory)
  useEffect(() => {
    if (!customFieldDefs.length || !Object.keys(categorizedData).length) return;
    const tabName = tabs[selectedTab]?.name || 'All';
    const cols = withSupplierContactColumns(getColumnsByProduct(tabName), tabName);
    setColumns([
      ...(getGridColumns(
        (categorizedData as CategorizedData)[tabName] || [],
        cols,
        `inventory_grid_${tabName}`
      ) as GroupedGridColDef[]),
      ...inventoryCustomFieldColumns(customFieldDefs)
    ]);
  }, [customFieldDefs]);

  useEffect(() => {
    if (userPreferences) {
      const customTabs: TabProps[] = customTabNames.map((t) => ({ name: t, count: 0, customView: true }));

      if (selectedCustomView && !customTabNames.includes(selectedCustomView)) {
        setSelectedCustomView('');
        handleTabChange(0, 'All');
      }

      setAllTabs(tabs.length ? tabs.concat(customTabs) : []);
    }
    // Keyed on the names, not the count: renaming a saved view replaces one in place, leaving the count
    // untouched while the tab name the dropdown selects by changes.
  }, [userPreferences, tabs, customTabNames]);

  useEffect(() => {
    if (tabs?.length) handleTabChange(0, tabs?.[0]?.name);
  }, [tabs?.length]);

  const tableCardActions = () => {
    const addInventory = <AddInventoryLink dataCy="new-outside-inventory" />;
    if (!locationsViewEnabled) return addInventory;

    return (
      <div className="flex items-center gap-6">
        <LabelledToggle
          id="display-as-locations-toggle"
          dataTestId="display-as-locations"
          label="Display as Locations"
          checked={displayAsLocations}
          onChange={handleDisplayAsLocationsChange}
        />
        {addInventory}
      </div>
    );
  };

  const handleTabChange = (index: number, name: string, customTab?: boolean) => {
    setSelectedCustomView(customTab ? name : '');
    // Custom views sit after the base tabs, so their index reads off the two lists allTabs is built
    // from. Taken from allTabs itself it would come from state written an effect behind them, and a
    // view picked the moment its name lands would resolve against the previous set.
    setSelectedTab(customTab ? tabs.length + customTabNames.indexOf(name) : index);

    const columns = withSupplierContactColumns(getColumnsByProduct(name), name);
    setColumns([
      ...(getGridColumns(
        categorizedData[name] || categorizedData['All'],
        columns,
        `inventory_grid_${name}`
      ) as GroupedGridColDef[]),
      ...inventoryCustomFieldColumns(customFieldDefsRef.current)
    ]);
  };

  // Guarantee a unique row key for DataGrid Pro. When the backend returns
  // inventory items with duplicate or missing `id` values, `getRowId` collapses
  // them to the same key and virtualization renders one row's data at multiple
  // positions while scrolling (UPDEV-6521).
  const gridRows = useMemo(() => {
    const seen: Record<string, number> = {};
    return (categorizedData['All'] || []).map((asset, idx) => {
      const base = asset.id || asset.sfId || `noid-${idx}`;
      const count = seen[base] || 0;
      seen[base] = count + 1;
      const _uiKey = count === 0 ? base : `${base}__${count}`;
      return { ...asset, _uiKey };
    });
  }, [categorizedData]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleNewTabModalOpen = () => {
    setTriggerSave(false);
    setCreateOpen(true);
  };

  const handleCustomFieldRowUpdate = useMemo(
    () =>
      makeCustomFieldRowUpdate<InventoryAsset>({
        entityType: 'Inventory',
        getDefinitions: () => customFieldDefsRef.current,
        // Update the inventory BehaviorSubject so that when user preferences
        // change (e.g. clicking Save), the combineLatest subscription re-fires with
        // the updated asset. The subscription is the single source of truth for
        // inventoryAssets / categorizedData, which avoids redundant state updates.
        persist: (asset) => {
          const currentInventory = getInventorySubject().getValue();
          getInventorySubject().next(currentInventory.map((a) => (a.id === asset.id ? asset : a)));
        }
      }),
    []
  );

  const handleCustomFieldRowUpdateError = useCallback(
    (error: unknown) => {
      setSnack({
        message: error instanceof Error ? error.message : FAILED_SAVE,
        open: true,
        type: 'error'
      });
    },
    [setSnack]
  );

  const customFieldCellIsEditable = useInventoryCustomFieldCellEditable({
    definitions: customFieldDefs,
    rows: gridRows,
    rowAccountId: inventoryRowAccountId,
    definitionsAccountId: getEffectiveAccountId(),
    unrestricted: isInternalUser(getUser()),
    denied: isCapabilityDenied(CustomFieldCapability.VALUES_UPSERT, getUser())
  });

  return (
    <PageLayout pageTitle={page}>
      <>
        <PageCard
          title="Dashboard"
          cardActions={<CustomizeDashboardDrawer title="Inventory" />}>
          <DashboardWidgets
            inventoryAssets={inventoryAssets}
            pageTitle={page}
            cases={[]}
            projects={[]}
            gridTab={allTabs[selectedTab]?.name || 'All'}
          />
        </PageCard>
        <PageCard
          childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
          title={page}
          cardActions={tableCardActions()}>
          {displayAsLocations ? (
            <LocationsTable projectsEnabled={featureFlags.projects === true} />
          ) : (
            <>
              <GridTabsHeader
                labels={tabs.map((tab) => `${tab.name} (${categorizedData[tab.name].length})`)}
                selectedTab={selectedTab}
                onTabChange={(index) => handleTabChange(index, allTabs?.[index]?.name)}>
                {tabs.length > 1 && (
                  <SavedViewControls
                    selectedCustomView={selectedCustomView}
                    customTabs={customTabNames}
                    onSelectView={(name) => handleTabChange(0, name, true)}
                    onSaveAsView={() => setTriggerSave(true)}
                    onScheduleReport={() => setOpen(true)}
                  />
                )}
              </GridTabsHeader>
              {allTabs.map((tab, index) => (
                <TabPanel
                  key={`${index}`}
                  index={index}
                  value={selectedTab}>
                  <UpstackDataGrid
                    dataCy="inventory-data-grid"
                    columns={columns}
                    gridSettings={(userPreferences?.[`inventory_grid_${tab.name}` as PrefKey] || {}) as GridPreferences}
                    handleNewTabModalOpen={handleNewTabModalOpen}
                    loadingData={inventoryLoading}
                    page={`inventory_grid_${tab.name}`}
                    pinnedColumns={{ left: ['details', 'account_id', 'sfId'] }}
                    rows={gridRows}
                    rowPrimaryKeys={['_uiKey']}
                    showSearch
                    title={`Inventory - ${tab.name}`}
                    triggerSave={triggerSave}
                    columnGroupSortOrder={ColumnGroupSortOrder}
                    processRowUpdate={handleCustomFieldRowUpdate}
                    onProcessRowUpdateError={handleCustomFieldRowUpdateError}
                    isCellEditable={customFieldCellIsEditable}
                  />
                </TabPanel>
              ))}
              {inventoryLoading && (
                <div className="absolute inset-0 w-full h-full flex items-center justify-center">
                  <ComponentSpinner />
                </div>
              )}
            </>
          )}
        </PageCard>
        <div className="h-2"></div>
        {(!displayAsLocations || detailId) && (
          <DetailsDrawer
            data={inventoryAssets?.find((ia: InventoryAsset) => ia?.id === detailId) || ({} as InventoryAsset)}
            showDrawer={!!detailId}
            link={`/inventory/${detailId}`}
            {...getMetaData('inventory')}
          />
        )}
      </>
      <ViewsModal
        open={open}
        handleClose={handleClose}
        type="inventory"
      />
      <NewViewModal
        open={createOpen}
        setOpen={setCreateOpen}
        sourceTab={allTabs[selectedTab]?.name || 'All'}
        type="inventory"
      />
    </PageLayout>
  );
};
